import { useEffect, useState } from 'react';
import { useActiveProject, useService } from '../../../../../hooks';
import { useAsync } from 'react-use';
import { AxiosResponse } from 'axios';
import {
  AttachmentSearchData,
  buildAttachmentSearchQuery,
} from './buildAttachmentSearchQuery';
import {
  generateQuestionnaireSearchQuery,
  QuestionnaireSearchData,
} from './buildQuestionnaireSearchQuery';
import { Attachment, PRIMARY_SURVEY_ID_SYSTEM } from './Attachment';
import {
  useReportErrorToBugsnag,
  useReportInfoToBugsnag,
} from '../../../../../util/bugsnagReporting';
import FhirSearchService from '../../../../../services/FhirSearchService';

const fss = new FhirSearchService();

export const useAttachmentArbiterState = (attachments?: string[]) => {
  const [project] = useActiveProject();
  const [attachmentsLoaded, setAttachmentsLoaded] = useState(false);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentMap, setAttachmentMap] = useState<
    Record<string, Attachment>
  >({});
  const [fssSearch] = useService(fss.search);

  const reportErrorToBugsnag = useReportErrorToBugsnag();
  const reportInfoToBugsnag = useReportInfoToBugsnag();

  const { error } = useAsync(async () => {
    if (attachmentsLoading || attachmentsLoaded || attachments === undefined) {
      return;
    }

    setAttachmentsLoading(true);

    const attachmentSearchResponse: AxiosResponse<AttachmentSearchData> =
      await fssSearch(
        project.id,
        buildAttachmentSearchQuery(attachments),
        undefined,
        { queryId: 'ai-tools-attachment-search' },
      );

    const primarySurveyIds = attachmentSearchResponse.data.hits.hits
      .map(
        hit =>
          hit._source.meta?.tag?.find(
            tag => tag.system === PRIMARY_SURVEY_ID_SYSTEM,
          )?.code,
      )
      .filter(Boolean);

    const questionnairesResponse: AxiosResponse<QuestionnaireSearchData> =
      await fssSearch(
        project.id,
        generateQuestionnaireSearchQuery(primarySurveyIds),
        undefined,
        { queryId: 'ai-tools-questionnaire-search' },
      );

    const attachmentMap: Record<string, Attachment> = {};

    attachmentSearchResponse.data.hits.hits.forEach(hit => {
      const attachment = Attachment.from(
        { questionnaireData: questionnairesResponse.data },
        hit._source,
      );
      attachmentMap[attachment.identifier] = attachment;
    });

    setAttachmentsLoaded(true);
    setAttachmentMap(attachmentMap);
    setAttachmentsLoading(false);
  }, [attachments, attachmentsLoaded, fssSearch, project.id]);

  useEffect(() => {
    if (error) {
      reportErrorToBugsnag(error);
    }
  }, [error, reportErrorToBugsnag]);

  useEffect(() => {
    if (attachments && attachmentsLoaded) {
      const loadedAttachments = new Set(Object.keys(attachmentMap));
      const missingAttachments = attachments?.filter(
        attachment => !loadedAttachments.has(attachment),
      );
      if (missingAttachments.length) {
        reportInfoToBugsnag(
          `Was not able to load all attachments: ${missingAttachments.join(
            ', ',
          )}`,
        );
      }
    }
  });

  return {
    loading: attachmentsLoading || !attachmentsLoaded,
    attachmentMap,
    setAttachmentMap,
  };
};
